import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../home/components/Header';
import Footer from '../../home/components/Footer';
import BlogRelatedStrip from '../../../components/feature/BlogRelatedStrip';
import BlogProgressBar from '../../../components/feature/BlogProgressBar';

const SITE_URL = 'https://hoosierlifeinsurance.com';

export default function LifeInsuranceForFamiliesIndianapolis() {
  useEffect(() => {
    window.scrollTo(0, 0);

    // Set canonical URL
    const SITE_URL_VAL = 'https://hoosierlifeinsurance.com';
    let canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.rel = 'canonical';
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.href = `${SITE_URL_VAL}/blog/life-insurance-for-families-indianapolis`;

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: 'Best Life Insurance for Indianapolis Families: Coverage, Costs & Options 2025',
        description: 'Complete guide to life insurance for Indianapolis families in 2025. Coverage amounts, best policy types, top carriers, and real pricing for Hoosier families at every stage.',
        author: {
          '@type': 'Organization',
          name: 'Hoosier Life Insurance',
          url: SITE_URL
        },
        publisher: { '@type': 'Organization', name: 'Hoosier Life Insurance', url: SITE_URL },
        datePublished: '2025-02-18T00:00:00Z',
        dateModified: new Date().toISOString(),
        url: `${SITE_URL}/blog/life-insurance-for-families-indianapolis`,
        mainEntityOfPage: 'https://hoosierlifeinsurance.com/blog/life-insurance-for-families-indianapolis',
        image: { '@type': 'ImageObject', url: 'https://public.readdy.ai/ai/img_res/f5f469951bff64acd1c352d6cae26267.jpg', width: 1200, height: 630 },
        timeRequired: 'PT11M',
        wordCount: 2200,
        inLanguage: 'en-US',
        isPartOf: {
          '@type': 'WebSite',
          url: SITE_URL
        },
        speakable: {
          '@type': 'SpeakableSpecification',
          cssSelector: ['h1', 'h2', 'details summary']
        }
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Hoosier Life Insurance',
        url: SITE_URL,
        logo: `${SITE_URL}/logo.png`,
        description: 'Independent life insurance agency serving Indianapolis and all of Indiana. Licensed specialists helping families find affordable coverage from 20+ top-rated carriers.',
        areaServed: { '@type': 'State', name: 'Indiana' },
        knowsAbout: ['Family Life Insurance Indianapolis', 'Term Life Insurance Indiana', 'Life Insurance for Families', 'Indianapolis Life Insurance']
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'What is the best life insurance for Indianapolis families?',
            acceptedAnswer: { '@type': 'Answer', text: 'For most Indianapolis families, a 20–30 year term life policy from Protective, Nationwide, or Mutual of Omaha offers the best combination of coverage and affordability. Young families with a mortgage and children typically need $750,000–$1.5 million in coverage from each income-earning spouse.' },
          },
          {
            '@type': 'Question',
            name: 'How much does life insurance cost for a family in Indianapolis?',
            acceptedAnswer: { '@type': 'Answer', text: 'A healthy Indianapolis couple in their 30s can each get $500,000 of 20-year term life coverage for $35–$55/month combined — less than $1.50/day to protect an entire family. Rates vary by age, health class, coverage amount, and term length.' },
          },
          {
            '@type': 'Question',
            name: 'Do both spouses in an Indianapolis family need life insurance?',
            acceptedAnswer: { '@type': 'Answer', text: 'Yes — both spouses should have separate life insurance policies, including stay-at-home parents. Replacing childcare, household management, and support services in Indiana costs $12,000–$18,000 per child per year. A $350,000 policy on a stay-at-home parent covers those costs for 15+ years.' },
          },
        ],
      },
    ];

    schemas.forEach((schema, idx) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = `schema-families-${idx}`;
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      schemas.forEach((_, idx) => {
        document.getElementById(`schema-families-${idx}`)?.remove();
      });
    };
  }, []);

  const familyScenarios = [
    {
      title: 'Young Couple, No Kids Yet',
      age: 'Ages 27–32',
      income: '$80,000 combined',
      rec: '$500K each / 30-year term',
      cost: '$28/mo combined',
      why: 'Lock in the lowest possible rates before starting a family. Adding a mortgage later won\'t change the premium.',
      icon: 'ri-couple-line',
    },
    {
      title: 'New Parents, First Home',
      age: 'Ages 30–35',
      income: '$90,000 combined',
      rec: '$750K each / 30-year term',
      cost: '$55/mo combined',
      why: 'The most critical coverage window. Your child depends 100% on your income for the next 20+ years.',
      icon: 'ri-parent-line',
    },
    {
      title: 'Growing Family, 2–3 Kids',
      age: 'Ages 35–42',
      income: '$110,000 combined',
      rec: '$1M each / 20-year term',
      cost: '$98/mo combined',
      why: 'College costs for 2–3 kids alone can exceed $300,000. A $1M policy covers income replacement AND tuition.',
      icon: 'ri-group-line',
    },
    {
      title: 'Established Family, Teens',
      age: 'Ages 42–50',
      income: '$130,000 combined',
      rec: '$500K each / 15-year term',
      cost: '$140/mo combined',
      why: 'With equity built and kids almost independent, you need less coverage. Focus on the final 10–15 years of the mortgage.',
      icon: 'ri-home-heart-line',
    },
  ];

  const coverageChecklist = [
    { item: 'Income replacement (10× salary minimum)', critical: true },
    { item: 'Mortgage payoff in full', critical: true },
    { item: 'College fund per child ($80K–$220K)', critical: true },
    { item: 'Final expenses ($10K–$15K)', critical: true },
    { item: 'Emergency fund replenishment', critical: false },
    { item: 'Surviving spouse\'s retirement supplement', critical: false },
    { item: 'Car loans and consumer debt', critical: false },
    { item: 'Childcare replacement (stay-at-home parent)', critical: true },
  ];

  return (
    <>
      <title>Best Life Insurance for Indianapolis Families 2026 | Hoosier Life</title>
      <meta name="description" content="Best life insurance for Indianapolis families in 2026 — real coverage amounts, policy recommendations by family stage, top carriers, and sample costs for Central Indiana households." />
      <meta name="keywords" content="life insurance for families Indianapolis, best life insurance Indianapolis families, family life insurance Indiana, Indianapolis family coverage 2025" />
      <meta property="og:title" content="Best Life Insurance for Indianapolis Families 2025 | Coverage & Costs" />
      <meta property="og:description" content="Real coverage amounts, policy recommendations, and sample costs for every family stage in Indianapolis." />
      <meta property="og:url" content={`https://hoosierlifeinsurance.com/blog/life-insurance-for-families-indianapolis`} />
      <link rel="canonical" href="https://hoosierlifeinsurance.com/blog/life-insurance-for-families-indianapolis" />
      <meta name="robots" content="index, follow" />
      <meta property="og:type" content="article" />
      <meta property="og:image" content="https://readdy.ai/api/search-image?query=happy%20Indianapolis%20family%20of%20four%20at%20home%20parents%20and%20two%20young%20children%20smiling%20bright%20living%20room%20clean%20modern%20warm%20natural%20light%20insurance%20planning%20documents&width=1200&height=630&seq=blog020og&orientation=landscape" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:image" content="https://readdy.ai/api/search-image?query=happy%20Indianapolis%20family%20of%20four%20at%20home%20parents%20and%20two%20young%20children%20smiling%20bright%20living%20room%20clean%20modern%20warm%20natural%20light%20insurance%20planning%20documents&width=1200&height=630&seq=blog020og&orientation=landscape" />

      <BlogProgressBar />
      <Header />

      <article className="min-h-screen bg-white">
        <div className="bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] text-white pt-28 pb-16">
          <div className="max-w-4xl mx-auto px-6">
            <Link to="/blog" className="inline-flex items-center text-white/60 hover:text-[#D97642] mb-6 transition-colors text-sm">
              <i className="ri-arrow-left-line mr-2"></i>Back to Blog
            </Link>
            <div className="flex items-center gap-3 mb-4">
              <span className="bg-[#D97642]/20 text-[#D97642] text-xs font-bold px-3 py-1.5 rounded-full">Term Life</span>
              <span className="text-white/50 text-xs">11 min read · February 18, 2025</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-5 leading-tight">Best Life Insurance for Indianapolis Families: 2025 Guide</h1>
            <p className="text-white/75 text-xl max-w-2xl leading-relaxed">Coverage amounts, best policies, top carriers, and real costs for Hoosier families — from first home to empty nest.</p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-6 py-12">

          {/* Key Takeaways — AI extraction box */}
          <div className="bg-[#1A3A52] text-white rounded-2xl p-7 mb-10">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
              <i className="ri-bookmark-3-fill text-[#D97642] text-xl"></i>
              Key Takeaways
            </h2>
            <ul className="space-y-2.5">
              {[
                'A healthy Indianapolis couple in their 30s can get $500,000 of 20-year term coverage each for just $35–$55/month combined',
                '40% of Indiana families with children have zero individual life insurance beyond a basic employer benefit — a critical gap',
                'Both spouses need separate policies — including stay-at-home parents. Indiana childcare runs $12,000–$18,000 per child per year',
                'Your coverage needs change at every family stage: a young family with 2 kids needs $1M+; an empty nester may only need $250K–$500K',
                'Independent agents shop 10+ carriers at no extra cost to you — same premium as going direct, with expert guidance',
              ].map((point, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-white/90">
                  <i className="ri-check-line text-[#D97642] mt-0.5 flex-shrink-0"></i>
                  {point}
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-[#1A3A52]/5 border-l-4 border-[#1A3A52] p-6 rounded-r-xl mb-10">
            <p className="font-bold text-gray-900 mb-1 flex items-center gap-2"><i className="ri-heart-2-line text-[#D97642]"></i>Key Stat for Indianapolis Families</p>
            <p className="text-gray-700 leading-relaxed">A healthy Indianapolis couple in their 30s can get <strong>$500,000 of 20-year term coverage each for just $35–$55/month combined</strong> — less than a Netflix subscription per person. Yet 40% of Indiana families with children have zero individual life insurance beyond a basic employer benefit. Before choosing a policy, use our <Link to="/blog/how-much-life-insurance-indiana" className="text-[#D97642] hover:text-[#C26638] font-semibold underline">Indiana coverage calculator</Link> to get the right number for your family.</p>
          </div>

          <p className="text-gray-700 text-lg leading-relaxed mb-8">
            Life insurance is the foundation of every family financial plan — and the piece most Indianapolis families get wrong. Too little coverage, wrong policy type, or simply waiting too long. This guide walks through each family stage with specific recommendations and real 2025 pricing from carriers serving Central Indiana.
          </p>

          {/* Coverage checklist */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Family Coverage Checklist: What to Include</h2>
            <div className="bg-gray-50 border-l-4 border-[#D97642] p-4 rounded-r-lg mb-5 text-gray-800 text-sm leading-relaxed">
              <strong>Direct answer:</strong> Every Indianapolis family should include income replacement (10× salary minimum), full mortgage payoff, college funding per child ($80K–$220K), and final expenses in their coverage total. Stay-at-home parents need separate coverage for childcare replacement — Indiana childcare costs $12,000–$18,000 per child per year.
            </div>
            <p className="text-gray-600 mb-5">Before picking a policy, total up what your family actually needs covered. For home protection specifically, read our <Link to="/blog/mortgage-protection-indianapolis" className="text-[#D97642] hover:text-[#C26638] font-semibold underline">Indianapolis mortgage protection guide</Link>. New parents have unique needs — see our <Link to="/blog/life-insurance-new-parents-indianapolis" className="text-[#D97642] hover:text-[#C26638] font-semibold underline">life insurance guide for new Indianapolis parents</Link>. For the most affordable options, compare carriers in our <Link to="/blog/cheapest-life-insurance-indiana" className="text-[#D97642] hover:text-[#C26638] font-semibold underline">cheapest life insurance Indiana guide</Link>:</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {coverageChecklist.map((c, i) => (
                <div key={i} className={`flex items-center gap-3 p-4 rounded-xl border ${c.critical ? 'bg-[#1A3A52]/5 border-[#1A3A52]/20' : 'bg-white border-gray-200'}`}>
                  <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 ${c.critical ? 'bg-[#D97642] text-white' : 'bg-gray-100 text-gray-500'}`}>
                    <i className={c.critical ? 'ri-error-warning-line' : 'ri-check-line'}></i>
                  </div>
                  <span className={`text-sm ${c.critical ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>{c.item}</span>
                  {c.critical && <span className="ml-auto text-xs bg-[#D97642]/10 text-[#D97642] font-bold px-2 py-0.5 rounded-full whitespace-nowrap">Critical</span>}
                </div>
              ))}
            </div>
          </section>

          {/* Family scenarios */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Recommendations by Family Stage</h2>
            <div className="bg-gray-50 border-l-4 border-[#D97642] p-4 rounded-r-lg mb-5 text-gray-800 text-sm leading-relaxed">
              <strong>Direct answer:</strong> A young couple without kids needs $500K each on a 30-year term ($28/month combined). New parents need $750K each ($55/month combined). A growing family with 2–3 kids needs $1M each ($98/month combined). Coverage needs and costs decrease once children are independent.
            </div>
            <p className="text-gray-600 mb-6">Every family is different. Here's what most Indianapolis families at each stage actually need:</p>
            <div className="space-y-5">
              {familyScenarios.map((s, i) => (
                <div key={i} className="bg-white border border-gray-200 rounded-xl p-6 hover:border-[#D97642] transition-colors">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#1A3A52]/5 rounded-xl flex-shrink-0">
                      <i className={`${s.icon} text-[#1A3A52] text-xl`}></i>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-4 mb-2 flex-wrap">
                        <div>
                          <h3 className="font-bold text-gray-900 text-lg">{s.title}</h3>
                          <span className="text-gray-500 text-sm">{s.age} · {s.income}</span>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-[#D97642] text-lg">{s.cost}</div>
                          <div className="text-xs text-gray-500">combined</div>
                        </div>
                      </div>
                      <div className="bg-[#1A3A52]/5 rounded-lg px-4 py-2 mb-3 text-sm">
                        <span className="font-semibold text-[#1A3A52]">Recommended: </span>
                        <span className="text-gray-700">{s.rec}</span>
                      </div>
                      <p className="text-gray-600 text-sm leading-relaxed">{s.why}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Why local agent */}
          <section className="mb-12 bg-amber-50 rounded-2xl p-8 border border-amber-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <i className="ri-map-pin-line text-[#D97642]"></i>
              Why Indianapolis Families Should Use a Local Agent
            </h2>
            <div className="bg-gray-50 border-l-4 border-[#D97642] p-4 rounded-r-lg mb-5 text-gray-800 text-sm leading-relaxed">
              <strong>Direct answer:</strong> A local independent agent compares 10+ top carriers at once, knows Indiana's health statistics and which carriers are most lenient, advocates for your family during claims, and costs you nothing extra — agents are paid by the insurance company, not you.
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { icon: 'ri-search-2-line', point: 'Shop 10+ carriers at once', detail: 'An independent local agent compares Protective, Nationwide, Mutual of Omaha, and more — not just one company.' },
                { icon: 'ri-heart-pulse-line', point: 'Indiana health expertise', detail: 'Indiana has above-average rates of diabetes and high blood pressure. Local agents know which carriers are most lenient.' },
                { icon: 'ri-customer-service-2-line', point: 'Claims advocacy when it matters', detail: 'When your family needs to file a claim, you\'ll have a local advocate who knows the process inside and out.' },
                { icon: 'ri-money-dollar-circle-line', point: 'Same cost as going direct', detail: 'Agents are paid by the insurance company — not by you. You get expert guidance at zero extra cost.' },
              ].map((p, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#D97642]/10 rounded-lg flex-shrink-0">
                    <i className={`${p.icon} text-[#D97642]`}></i>
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 text-sm">{p.point}</p>
                    <p className="text-gray-600 text-xs leading-relaxed mt-0.5">{p.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Author Bio */}
          <div className="border border-gray-200 rounded-2xl p-7 mb-10 flex items-start gap-5">
            <div className="w-14 h-14 flex items-center justify-center bg-[#1A3A52] rounded-full flex-shrink-0">
              <i className="ri-shield-user-line text-white text-2xl"></i>
            </div>
            <div>
              <p className="font-bold text-gray-900 text-base mb-1">Written by the Licensed Life Insurance Specialists at Hoosier Life Insurance</p>
              <p className="text-sm text-gray-600 leading-relaxed mb-2">
                Our team of licensed Indiana life insurance agents specializes in helping Indianapolis families at every stage find the right coverage. We are independent agents — not tied to any single carrier — so we shop 20+ A-rated companies to find your best rate. Coverage recommendations and rate data are based on current 2025 Indiana market conditions and are reviewed and updated regularly.
              </p>
              <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                <span className="flex items-center gap-1"><i className="ri-checkbox-circle-line text-[#D97642]"></i>Licensed in Indiana</span>
                <span className="flex items-center gap-1"><i className="ri-checkbox-circle-line text-[#D97642]"></i>Independent — 20+ Carriers</span>
                <span className="flex items-center gap-1"><i className="ri-checkbox-circle-line text-[#D97642]"></i>Last reviewed March 2025</span>
              </div>
            </div>
          </div>

          {/* CTA */}
          <section className="bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] rounded-2xl p-8 text-center text-white mb-12">
            <h2 className="text-2xl font-bold mb-3">Protect Your Indianapolis Family Today</h2>
            <p className="text-white/70 mb-6 max-w-xl mx-auto">Get a personalized recommendation based on your family's exact situation. Free, no-obligation, takes 5 minutes.</p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/instant-quote" className="px-6 py-3 bg-[#D97642] text-white rounded-lg font-bold hover:bg-[#C26638] transition-colors whitespace-nowrap">Get Family Quote</Link>
              <Link to="/book-appointment" className="px-6 py-3 bg-white/10 text-white rounded-lg font-bold hover:bg-white/20 border border-white/20 transition-colors whitespace-nowrap">Book Free Consultation</Link>
            </div>
            <p className="text-white/50 text-xs mt-4">Most families are fully covered within 2–4 weeks of their first quote.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">Related Guides</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { to: '/blog/how-much-life-insurance-indiana', icon: 'ri-calculator-line', title: 'Coverage Calculator', desc: 'Find your exact coverage number' },
                { to: '/blog/term-life-insurance-indianapolis', icon: 'ri-shield-check-line', title: 'Term Life Guide', desc: 'Complete guide to term policies' },
                { to: '/blog/life-insurance-new-parents-indianapolis', icon: 'ri-parent-line', title: 'New Parents Guide', desc: 'Coverage for growing families' },
              ].map(r => (
                <Link key={r.to} to={r.to} className="group border border-gray-200 rounded-xl p-5 hover:border-[#D97642] transition-colors">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#1A3A52]/5 rounded-lg mb-3">
                    <i className={`${r.icon} text-[#1A3A52] text-xl`}></i>
                  </div>
                  <p className="font-bold text-gray-900 text-sm mb-1 group-hover:text-[#D97642] transition-colors">{r.title}</p>
                  <p className="text-gray-500 text-xs">{r.desc}</p>
                </Link>
              ))}
            </div>
          </section>
        </div>
      </article>

      <BlogRelatedStrip currentSlug="life-insurance-for-families-indianapolis" />
      <Footer />
    </>
  );
}
